import backendConfig from "../config/backendConfig.ts";
import { adminHeaders, handleUnauthorized } from "./admin.service.ts";

export type Granularity = "auto" | "day" | "month" | "year";

export type Totals = {
  revenue: number;
  orders: number;
  pieces: number;
  aov: number;
  customers: number;
};

export type RegionRow = { name: string; orders: number; revenue: number };

export type Overview = {
  range: { from: string; to: string; granularity: Exclude<Granularity, "auto">; firstOrder: string };
  kpis: Totals & {
    newCustomers: number;
    repeatCustomers: number;
    itemsRevenue: number;
    deliveryFees: number;
    discounts: number;
    cancelled: number;
    cancelRate: number;
    collected: number;
    inPipeline: number;
  };
  previous: Totals & { from: string; to: string };
  snapshot: { today: Totals; month: Totals; year: Totals; all: Totals };
  status: Array<{ name: string; count: number; value: number }>;
  series: Array<{ key: string; label: string; revenue: number; orders: number; pieces: number }>;
  topProducts: Array<{ id: string; name: string; image: string; pieces: number; revenue: number; orders: number }>;
  productCount: number;
  byCategory: Array<{ name: string; pieces: number; revenue: number }>;
  bySize: Array<{ size: string; pieces: number }>;
  cities: Array<RegionRow & { province: string }>;
  cityCount: number;
  provinces: RegionRow[];
  countries: RegionRow[];
  weekday: Array<{ name: string; orders: number; revenue: number }>;
  hours: Array<{ hour: number; orders: number }>;
  inventory: {
    products: number;
    outOfStock: number;
    lowStock: number;
    attention: Array<{ id: string; name: string; image: string; stock: number; out: boolean }>;
  };
  recent: Array<{
    id: string;
    name: string;
    city: string;
    total: number;
    pieces: number;
    status: string;
    createdAt: string;
  }>;
};

export const getOverview = async (params: {
  from?: string;
  to?: string;
  range?: "all";
  granularity?: Granularity;
}): Promise<{ success: boolean; data?: Overview; error?: string }> => {
  try {
    const query = new URLSearchParams();
    if (params.range) query.set("range", params.range);
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.granularity && params.granularity !== "auto") {
      query.set("granularity", params.granularity);
    }
    const response = await fetch(
      `${backendConfig.API_BASE_URL}${backendConfig.ENDPOINTS.STATS}/overview?${query}`,
      { headers: adminHeaders() },
    );
    const result = await response.json();
    if (!response.ok || !result.success) {
      handleUnauthorized(response.status);
      return { success: false, error: result.error || `HTTP ${response.status}` };
    }
    return { success: true, data: result.data };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
